import type {
  Filter,
  FilterDataMultiSelect,
  FilterDataOption,
  LabelComponent,
  SimpleFilterValueList,
} from '@/app/common/filters/core/filtersTypes.ts';
import { FILTER_TYPE } from '@/app/common/filters/core/filtersTypes.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import { NO_STAR_RATING_VALUE, STARS_VALUES } from '@/app/common/filters/core/starsValues.ts';
import { counterOf, disabledAtZero } from '@/app/common/filters/groups/optionCounts.ts';

type CreateStarsFilterOption = {
  value: string;
  selectedValues: string[];
  valueList?: SimpleFilterValueList;
};
const createStarsFilterOption = ({ value, selectedValues, valueList }: CreateStarsFilterOption): FilterDataOption => {
  const valueNumber = parseInt(value, 10);
  const number = !Number.isNaN(valueNumber) && valueNumber >= 0 && valueNumber <= 5 ? valueNumber : 0;
  const labelComponent: LabelComponent = {
    componentName: 'stars',
    props: {
      value: `${number}`,
    },
  };

  const selected = selectedValues.includes(value);
  const counter = counterOf(valueList, value);

  return {
    label: `${value}`,
    labelComponent,
    selected,
    disabled: disabledAtZero(selected, counter),
    value,
    counter,
  };
};

// No labelComponent: <Stars stars={0}/> renders null, so LabelNode must use the text branch.
type CreateNoStarRatingOption = {
  label: string;
  selectedValues: string[];
  valueList?: SimpleFilterValueList;
};
const createNoStarRatingOption = ({ label, selectedValues, valueList }: CreateNoStarRatingOption): FilterDataOption => {
  const selected = selectedValues.includes(NO_STAR_RATING_VALUE);
  const counter = counterOf(valueList, NO_STAR_RATING_VALUE);

  return {
    label,
    selected,
    disabled: disabledAtZero(selected, counter),
    value: NO_STAR_RATING_VALUE,
    counter,
  };
};

// 1-2 star hotels are being deactivated (MPH-760): searches may legitimately have none,
// so a zero-count option would only offer a selection with no results. A value selected
// in the URL is never hidden nor disabled — the user must still be able to unselect it
// (StarsPill also derives its label from selected options).
// Evo keeps all five visible for the modal's fixed rectangle row, disabling empty ones.
const STARS_VALUES_HIDDEN_WHEN_EMPTY = ['2', '1'];

const isStarsOptionHidden = ({
  value,
  selectedValues,
  valueList,
  hiddenWhenEmpty,
}: CreateStarsFilterOption & { hiddenWhenEmpty: string[] }): boolean =>
  hiddenWhenEmpty.includes(value) && !!valueList && !valueList[value] && !selectedValues.includes(value);

type CreateStarsFilterOptions = {
  selectedValues: string[];
  valueList?: SimpleFilterValueList;
  useEvoFilters?: boolean;
  noRatingLabel?: string;
};
const createStarsFilterOptions = ({
  selectedValues,
  valueList,
  useEvoFilters,
  noRatingLabel,
}: CreateStarsFilterOptions): FilterDataOption[] => {
  const hiddenWhenEmpty = useEvoFilters ? [] : STARS_VALUES_HIDDEN_WHEN_EMPTY;

  const starsOptions = STARS_VALUES.filter(
    (value) => !isStarsOptionHidden({ value, selectedValues, valueList, hiddenWhenEmpty })
  ).map((value) => createStarsFilterOption({ value, selectedValues, valueList }));

  // Last, so leadingLabel and the session leader stay on a numeric category.
  if (!useEvoFilters || !noRatingLabel) return starsOptions;

  return [...starsOptions, createNoStarRatingOption({ label: noRatingLabel, selectedValues, valueList })];
};

type CreateStarsFilters = {
  searchParams: SearchParams;
  valueList?: SimpleFilterValueList;
  useEvoFilters?: boolean;
  noRatingLabel?: string;
};
export const createStarsFilters = ({
  searchParams,
  valueList,
  useEvoFilters,
  noRatingLabel,
}: CreateStarsFilters): Filter[] => {
  const selectedValues = searchParams[URLParameters.STARS]?.split(',') || [];

  const options = createStarsFilterOptions({ selectedValues, valueList, useEvoFilters, noRatingLabel });
  const starsFilter: Filter = {
    name: FILTER_NAME.STARS,
    type: FILTER_TYPE.MULTI_SELECT,
    data: {
      options,
    } as FilterDataMultiSelect,
  };

  return [...[starsFilter]].filter((f) => !!f);
};
